import { ReactNode } from 'react';
import { Tag, Tooltip } from 'antd';
import UserAvatar from 'components/user-avatar';
import type { StudioMemberBase } from '../contract';
import { INACTIVE_TAG, MEMBER_TYPE_COLOR, MEMBER_TYPE_LABEL } from '../labels';
import { displayName, shortUid } from '../format';
import styles from '../styles.module.scss';

export interface MemberCellProps {
  /** Tên hiển thị (nickName); không có thì dùng name → @TikTok ID → UID …1234. */
  nickName?: string | null;
  name?: string | null;
  username?: string | null;
  uid?: string | null;
  avatar?: string | null;
  /** Thẻ loại: Creator / Nhân viên / Trợ lý / Quản lý. */
  memberType?: StudioMemberBase['memberType'] | null;
  /** Thẻ "Không còn trong file TikTok". */
  inactive?: boolean;
  /** Thẻ "Kênh <tên nhóm>" khi UID của người này là kênh của một Nhóm nhảy. */
  channelOf?: { name: string } | null;
  /** Dòng phụ; mặc định "@TikTok ID · UID …1234". Truyền null để bỏ. */
  sub?: ReactNode | null;
  /** Thẻ thêm (vd. "Đang chờ Admin", "Nhóm trưởng"). */
  extra?: ReactNode;
  /** Bỏ ảnh (ô hẹp). */
  hideAvatar?: boolean;
  size?: number;
}

/** Ảnh + tên + thẻ loại của một thành viên Studio (mục 8.6, 8.7.5). Dùng chung cho bảng và ô chọn. */
const MemberCell = ({
  nickName,
  name,
  username,
  uid,
  avatar,
  memberType,
  inactive,
  channelOf,
  sub,
  extra,
  hideAvatar,
  size = 36,
}: MemberCellProps) => {
  const title = displayName({ nickName, name, username, uid });
  const defaultSub = [username ? `@${username}` : '', uid ? `UID ${shortUid(uid)}` : '']
    .filter(Boolean)
    .join(' · ');
  const subLine = sub === undefined ? defaultSub : sub;
  const hasTags = !!memberType || !!inactive || !!channelOf || !!extra;

  return (
    <div className={styles.person}>
      {!hideAvatar && (
        <UserAvatar size={size} src={avatar} style={{ flex: 'none' }} />
      )}
      <div className={styles.personText}>
        <span className={styles.personName}>{title}</span>
        {subLine && (
          <Tooltip title={uid ? `UID ${uid}` : undefined}>
            <span className={styles.personSub}>{subLine}</span>
          </Tooltip>
        )}
        {hasTags && (
          <span className={styles.tags}>
            {memberType && (
              <Tag color={MEMBER_TYPE_COLOR[memberType]}>{MEMBER_TYPE_LABEL[memberType]}</Tag>
            )}
            {inactive && <Tag color="default">{INACTIVE_TAG}</Tag>}
            {channelOf && <Tag color="purple">Kênh {channelOf.name}</Tag>}
            {extra}
          </span>
        )}
      </div>
    </div>
  );
};

export default MemberCell;
